import { GuideSection } from '@/components/guides/GuideSection';
import { GuideSteps, GuideStep } from '@/components/guides/GuideSteps';
import { GuideCallout } from '@/components/guides/GuideCallout';
import { GuideAction } from '@/components/guides/GuideAction';
import { GuideScreenshot } from '@/components/guides/GuideScreenshot';

const ROLES = [
  {
    name: 'Admin',
    blurb:
      'Full access. Invite teammates, manage candidates, issue licenses, see billing, request refunds.',
    who: 'One or two trusted people on your team.',
  },
  {
    name: 'Team Lead',
    blurb:
      'Manage their team, add candidates, and issue NeuraX licenses via scheduling. Can see billing but not invite others.',
    who: 'Managers of recruiter pods.',
  },
  {
    name: 'Recruiter',
    blurb: "Add their own candidates and schedule sessions. Can't see company billing.",
    who: 'Day-to-day operators.',
  },
  {
    name: 'Viewer',
    blurb: 'Read-only access across the portal. Useful for observers and auditors.',
    who: 'Stakeholders who want visibility without the ability to change anything.',
  },
];

export function InviteTeamSection() {
  return (
    <GuideSection
      id="invite-team"
      eyebrow="Team (admin only)"
      title="Invite your team"
      description="Admins can bring teammates onboard with a magic-link invite. No passwords to share, no seats to purchase separately."
    >
      <GuideScreenshot
        src="/guides/employees-list.png"
        alt="Employees page showing a table of teammates with name, email, role, status, and joined date. 'Invite teammate' button top right"
        caption="The Employees page lists everyone on your team. The Invite teammate button (top right) is admin-only."
      />

      <GuideSteps>
        <GuideStep number={1} title="Open Employees">
          <p>
            Click <strong>Employees</strong> in the sidebar. The button is only visible to admins —
            if you can't see it, ask an admin on your team.
          </p>
          <GuideAction href="/employees">Open Employees</GuideAction>
        </GuideStep>

        <GuideStep number={2} title="Click “Invite teammate”">
          <p>Opens a dialog. Three required fields plus role.</p>
        </GuideStep>

        <GuideStep number={3} title="Fill in the invite">
          <GuideScreenshot
            src="/guides/invite-teammate-dialog.png"
            alt="Invite teammate dialog with full name and work email fields, followed by four role options: Admin, Team Lead, Recruiter, Viewer — each with a one-line description"
            caption="The Invite teammate dialog. Pick a role; each one is described inline so you can't get it wrong."
          />
          <ul className="ml-4 list-disc space-y-1">
            <li>
              <strong>Full name</strong> — for display in the team list.
            </li>
            <li>
              <strong>Work email</strong> — the magic link lands here.
            </li>
            <li>
              <strong>Role</strong> — see the table below for what each level unlocks.
            </li>
          </ul>
        </GuideStep>

        <GuideStep number={4} title="Send">
          <p>
            We generate a one-time invite token (valid for <strong>7 days</strong>) and send them an
            email with a magic link. When they click it, they pick their own password and land on
            your Overview.
          </p>
        </GuideStep>

        <GuideStep number={5} title="If the invite expires">
          <p>
            Invite tokens expire after 7 days. If yours does, just invite again — it generates a
            fresh token.
          </p>
        </GuideStep>
      </GuideSteps>

      <div className="space-y-3">
        <h3 className="font-display text-base font-semibold text-white">Roles</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {ROLES.map((role) => (
            <div key={role.name} className="glass-card p-4">
              <p className="text-sm font-semibold text-white">{role.name}</p>
              <p className="mt-1 text-xs text-muted-foreground">{role.blurb}</p>
              <p className="mt-2 text-[11px] uppercase tracking-wider text-neurax-electric/70">
                Good fit:{' '}
                <span className="text-white/70 normal-case tracking-normal">{role.who}</span>
              </p>
            </div>
          ))}
        </div>
      </div>

      <GuideCallout variant="info" title="Status badges on the team list">
        <p>
          <strong>Active</strong> (mint) means the teammate has clicked the invite link and set a
          password. <strong>Pending</strong> (amber) means the invite is out but hasn't been
          accepted yet. Hover the row to see when the invite was sent.
        </p>
      </GuideCallout>

      <GuideCallout variant="warning" title="Removing teammates">
        <p>
          The portal doesn't yet have a "remove member" button. When someone leaves Wolf, email
          F1Jobs support with the teammate's email and we'll deactivate them immediately. Their
          historical actions stay in the audit log.
        </p>
      </GuideCallout>
    </GuideSection>
  );
}
